import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Search,
  Plus,
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
} from 'lucide-react';

import AdminRoute from '@/components/auth/AdminRoute';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAffiliatesList,
  useInviteAffiliate,
  useCreateAffiliate,
  useAffiliateInvitations,
  useUpdateAffiliateStatus,
} from '@/hooks/useAffiliates';
import type { Affiliate } from '@/../../shared/types/affiliate';

// Extended type for affiliates that may have stats wrapper
type AffiliateWithStats = Affiliate & {
  stats?: {
    totalConversions?: number;
    pendingEarnings?: number;
    totalPaid?: number;
  };
};

// Type for affiliate invitations
type AffiliateInvitation = {
  id: string;
  email: string;
  displayName?: string;
  invitedAt?: {
    toDate: () => Date;
  };
  status?: string;
};

export const Route = createFileRoute('/admin/affiliates')({
  component: AdminAffiliatesPage,
});

function AdminAffiliatesPage() {
  return (
    <AdminRoute>
      <AdminAffiliatesContent />
    </AdminRoute>
  );
}

function AdminAffiliatesContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [inviteEmail, setInviteEmail] = useState('');

  // Hooks
  const { data: affiliates = [], isLoading } = useAffiliatesList();
  const { data: invitations = [], isLoading: isLoadingInvitations } =
    useAffiliateInvitations();
  const inviteAffiliate = useInviteAffiliate();
  const createAffiliate = useCreateAffiliate();
  const updateAffiliateStatus = useUpdateAffiliateStatus();

  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    paymentEmail: '',
    affiliateId: '',
    commissionRate: '0.25',
  });

  const filteredAffiliates = affiliates.filter(
    (affiliate: AffiliateWithStats) => {
      const matchesSearch =
        searchTerm === '' ||
        affiliate.displayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        affiliate.affiliateId
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        affiliate.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        selectedStatus === 'all' || affiliate.status === selectedStatus;

      return matchesSearch && matchesStatus;
    }
  );

  // Calculate stats from real affiliate data
  const stats = affiliates.reduce(
    (
      acc: {
        activeCount: number;
        totalConversions: number;
        pendingPayouts: number;
        totalPaid: number;
      },
      affiliate: AffiliateWithStats
    ) => {
      // Count active affiliates
      if (affiliate.status === 'active') {
        acc.activeCount++;
      }

      // Sum conversions
      acc.totalConversions += affiliate.stats?.totalConversions || 0;

      // Sum pending payouts
      const pending = affiliate.stats?.pendingEarnings || 0;
      acc.pendingPayouts += typeof pending === 'number' ? pending : 0;

      // Sum total paid
      const paid = affiliate.stats?.totalPaid || 0;
      acc.totalPaid += typeof paid === 'number' ? paid : 0;

      return acc;
    },
    {
      activeCount: 0,
      totalConversions: 0,
      pendingPayouts: 0,
      totalPaid: 0,
    }
  );

  const handleInviteAffiliate = () => {
    inviteAffiliate.mutate(
      { email: inviteEmail },
      {
        onSuccess: () => {
          setIsInviteDialogOpen(false);
          setInviteEmail('');
        },
      }
    );
  };

  const handleCreateAffiliate = () => {
    createAffiliate.mutate(
      {
        displayName: formData.displayName,
        email: formData.email,
        paymentEmail: formData.paymentEmail || formData.email,
        affiliateId: formData.affiliateId || undefined,
        commissionRate: parseFloat(formData.commissionRate),
        sendWelcomeEmail: true,
      },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          setFormData({
            displayName: '',
            email: '',
            paymentEmail: '',
            affiliateId: '',
            commissionRate: '0.25',
          });
        },
      }
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Affiliate Management
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage affiliate partners and track commissions
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsInviteDialogOpen(true)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Invite Affiliate
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Affiliate
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Affiliates
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeCount}</div>
              <p className="text-xs text-muted-foreground">
                {affiliates.length} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Conversions
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalConversions}</div>
              <p className="text-xs text-muted-foreground">
                All time referrals
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Payouts
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${stats.pendingPayouts.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">Awaiting payout</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Paid Out
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${stats.totalPaid.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Pending Invitations
              </CardTitle>
              <CardDescription>
                Affiliates who have been invited but haven't accepted yet
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingInvitations ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-3">
                  {(invitations as AffiliateInvitation[]).map(
                    (invitation: AffiliateInvitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{invitation.email}</p>
                          {invitation.displayName && (
                            <p className="text-sm text-muted-foreground">
                              {invitation.displayName}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            Invited{' '}
                            {invitation.invitedAt?.toDate
                              ? new Date(
                                  invitation.invitedAt.toDate()
                                ).toLocaleDateString()
                              : 'recently'}
                          </p>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      </div>
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search by name, code, or email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="md:w-64">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Affiliates List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredAffiliates.map((affiliate: AffiliateWithStats) => (
              <motion.div
                key={affiliate.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">
                          {affiliate.displayName}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <code className="text-sm bg-muted px-2 py-0.5 rounded">
                            {affiliate.affiliateId}
                          </code>
                          <span>•</span>
                          <span>{affiliate.email}</span>
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(affiliate.status)}>
                          {getStatusIcon(affiliate.status)}
                          <span className="ml-1">{affiliate.status}</span>
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Commission Rate
                        </p>
                        <p className="text-lg font-semibold">
                          {(affiliate.commissionRate * 100).toFixed(0)}%
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Conversions
                        </p>
                        <p className="text-lg font-semibold">
                          {affiliate.totalConversions}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Total Earned
                        </p>
                        <p className="text-lg font-semibold">
                          ${affiliate.totalEarnings.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Pending</p>
                        <p className="text-lg font-semibold text-amber-600">
                          ${affiliate.pendingEarnings.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">
                          Paid Out
                        </p>
                        <p className="text-lg font-semibold text-green-600">
                          ${affiliate.paidEarnings.toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm">
                        View Details
                      </Button>
                      <Button variant="outline" size="sm">
                        View Referrals
                      </Button>
                      {affiliate.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-orange-600 hover:text-orange-700"
                          onClick={() =>
                            updateAffiliateStatus.mutate({
                              affiliateId: affiliate.id,
                              status: 'suspended',
                            })
                          }
                          disabled={updateAffiliateStatus.isPending}
                        >
                          Suspend
                        </Button>
                      )}
                      {affiliate.status === 'suspended' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-600 hover:text-green-700"
                          onClick={() =>
                            updateAffiliateStatus.mutate({
                              affiliateId: affiliate.id,
                              status: 'active',
                            })
                          }
                          disabled={updateAffiliateStatus.isPending}
                        >
                          Reactivate
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() =>
                          updateAffiliateStatus.mutate({
                            affiliateId: affiliate.id,
                            status: 'inactive',
                          })
                        }
                        disabled={updateAffiliateStatus.isPending}
                      >
                        Terminate
                      </Button>
                      {affiliate.pendingEarnings > 0 && (
                        <Button size="sm" className="ml-auto">
                          Process Payout
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {/* No Results */}
            {filteredAffiliates.length === 0 && !isLoading && (
              <Card className="text-center py-8">
                <CardContent>
                  <p className="text-muted-foreground">
                    No affiliates match your search criteria
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Create Affiliate Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Affiliate</DialogTitle>
              <DialogDescription>
                Add a new affiliate partner to your program
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  placeholder="Jane Doe"
                  value={formData.displayName}
                  onChange={e =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="jane@example.com"
                  value={formData.email}
                  onChange={e =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="paymentEmail">Payment Email (optional)</Label>
                <Input
                  id="paymentEmail"
                  type="email"
                  placeholder="Same as email if not provided"
                  value={formData.paymentEmail}
                  onChange={e =>
                    setFormData({ ...formData, paymentEmail: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="affiliateId">Affiliate Code (optional)</Label>
                <Input
                  id="affiliateId"
                  placeholder="Auto-generated if not provided"
                  value={formData.affiliateId}
                  onChange={e =>
                    setFormData({ ...formData, affiliateId: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  3-20 alphanumeric characters
                </p>
              </div>
              <div>
                <Label htmlFor="commissionRate">Commission Rate</Label>
                <Select
                  value={formData.commissionRate}
                  onValueChange={value =>
                    setFormData({ ...formData, commissionRate: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.10">10%</SelectItem>
                    <SelectItem value="0.15">15%</SelectItem>
                    <SelectItem value="0.20">20% (Default)</SelectItem>
                    <SelectItem value="0.25">25%</SelectItem>
                    <SelectItem value="0.30">30%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateAffiliate}
                disabled={createAffiliate.isPending}
              >
                {createAffiliate.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Affiliate'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite Affiliate Dialog */}
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Invite Affiliate</DialogTitle>
              <DialogDescription>
                Send an invitation email to join your affiliate program
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="inviteEmail">Email Address</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  placeholder="affiliate@example.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                />
              </div>
              <Alert>
                <Mail className="h-4 w-4" />
                <AlertDescription>
                  An invitation email will be sent with instructions to join
                  your affiliate program.
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsInviteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInviteAffiliate}
                disabled={inviteAffiliate.isPending || !inviteEmail}
              >
                {inviteAffiliate.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
